import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { acceptLegalAgreements } from "api";
import { applyLegalAgreementAcceptanceToPermissions } from "helpers/legalAgreements";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import errorToast from "helpers/errorToast";

type FormValues = {
  termsAndConditions: boolean;
  privacyPolicy: boolean;
};

interface ITermsAndConditions {
  show: boolean;
  onAccepted: () => void;
  onPermissionsUpdated: (permissions: Record<string, unknown>) => void;
}

function TermsAndConditions({ show, onAccepted, onPermissionsUpdated }: ITermsAndConditions) {
  const intl = useIntl();
  const methods = useForm<FormValues>();
  const { handleSubmit, watch } = methods;
  const termsAndConditions = watch("termsAndConditions");
  const privacyPolicy = watch("privacyPolicy");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!termsAndConditions || !privacyPolicy) return;
    setIsSubmitting(true);

    try {
      const response = await acceptLegalAgreements();
      const storedRaw = localStorage.getItem("cargobase_userpermissions");
      const stored = storedRaw ? JSON.parse(storedRaw) : {};
      const updated = applyLegalAgreementAcceptanceToPermissions(stored, response.data);
      onPermissionsUpdated(updated);
      onAccepted();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        const storedRaw = localStorage.getItem("cargobase_userpermissions");
        const stored = storedRaw ? JSON.parse(storedRaw) : {};
        onPermissionsUpdated(
          applyLegalAgreementAcceptanceToPermissions(stored, {
            agreed_to_terms_condition: true,
            agreed_to_privacy_policy: true,
          }),
        );
        onAccepted();
        return;
      }
      errorToast(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title={intl.formatMessage({
        id: "termsAndConditions.termsAndConditions",
        defaultMessage: "Terms and Conditions",
      })}
      show={show}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Checkbox
            name="termsAndConditions"
            checkboxLabel={
              <FormattedMessage
                id="termsAndConditions.termsAgreement"
                defaultMessage="I have read and agree to Cargobase's {termsLink}."
                values={{
                  termsLink: (
                    <a href="https://www.cargobase.com/terms-and-conditions" target="_blank" rel="noreferrer">
                      <FormattedMessage id="login.termsAndConditions" defaultMessage="Terms and Conditions" />
                    </a>
                  ),
                }}
              />
            }
            providerSections
          />
          <div className="mt-3">
            <Checkbox
              name="privacyPolicy"
              checkboxLabel={
                <FormattedMessage
                  id="termsAndConditions.privacyAgreement"
                  defaultMessage="I understand my personal data will be processed in accordance with Cargobase's {privacyLink}."
                  values={{
                    privacyLink: (
                      <a href="https://www.cargobase.com/privacy-policy" target="_blank" rel="noreferrer">
                        <FormattedMessage id="settings.documents-agreements.privacyPolicy" defaultMessage="Privacy Policy" />
                      </a>
                    ),
                  }}
                />
              }
              providerSections
            />
          </div>

          <Button
            isLoading={isSubmitting}
            disabled={!termsAndConditions || !privacyPolicy}
            className="ml-auto mt-15"
            type="submit"
          >
            <FormattedMessage id="termsAndConditions.accept" defaultMessage="Accept" />
          </Button>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default TermsAndConditions;
